/**
 * MessageList -- Monarch Chat Message List
 *
 * Scrollable container for chat messages. Automatically scrolls to the
 * latest message when new content arrives, unless the user has scrolled
 * up to review history.
 *
 * STYLE: Warm dark palette, gold accents, rounded corners.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { ChatMessage } from '@/stores/chatStore';
import MessageItem from './ChatMessage';

// ---------------------------------------------------------------------------
// Date Separator
// ---------------------------------------------------------------------------

function DateSeparator({ date }: { date: string }) {
  const d = new Date(date);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  let label: string;
  if (d.toDateString() === today.toDateString()) {
    label = 'Today';
  } else if (d.toDateString() === yesterday.toDateString()) {
    label = 'Yesterday';
  } else {
    label = d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
  }

  return (
    <div className="flex items-center gap-4 py-4">
      <div className="flex-1 h-px bg-border" />
      <span className="text-[10px] font-semibold text-text-3 uppercase tracking-wider">
        {label}
      </span>
      <div className="flex-1 h-px bg-border" />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty State
// ---------------------------------------------------------------------------

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-16">
      <div className="w-16 h-16 rounded-xl mb-6 bg-bg-2 border border-border flex items-center justify-center">
        <svg className="w-8 h-8 text-monarch-gold" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 3L4 7v10l8 4 8-4V7l-8-4z"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinejoin="round"
          />
          <circle cx="12" cy="12" r="3" fill="currentColor" opacity="0.6" />
        </svg>
      </div>
      <h3 className="text-lg font-bold text-text-2 mb-2">
        Monarch AI
      </h3>
      <p className="text-sm text-text-3 max-w-xs leading-relaxed">
        Ask about website traffic, booking funnels, guest engagement, and channel performance.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MessageList
// ---------------------------------------------------------------------------

interface MessageListProps {
  messages: ChatMessage[];
}

export default function MessageList({ messages }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);

  /**
   * Detect whether the user is scrolled near the bottom.
   */
  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distFromBottom < 80;
  }, []);

  /**
   * Auto-scroll to the bottom when new messages arrive
   * (only if the user hasn't scrolled up).
   */
  useEffect(() => {
    if (isNearBottomRef.current && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages.length, messages[messages.length - 1]?.content]);

  if (messages.length === 0) {
    return <EmptyState />;
  }

  // Group messages by date for separators
  let lastDate = '';

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto px-3 sm:px-6 py-3 sm:py-4 scroll-smooth [mask-image:linear-gradient(to_bottom,transparent,black_20px,black_calc(100%-20px),transparent)]"
    >
      {messages.map((msg, i) => {
        const msgDate = new Date(msg.timestamp).toDateString();
        const showSeparator = msgDate !== lastDate;
        lastDate = msgDate;

        return (
          <div key={msg.id || `msg-${i}`}>
            {showSeparator && <DateSeparator date={new Date(msg.timestamp).toISOString()} />}
            <MessageItem message={msg} />
          </div>
        );
      })}

      {/* Auto-scroll anchor */}
      <div ref={bottomRef} />
    </div>
  );
}
